import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Headphones,
  Play,
  Pause,
  Clock,
  Volume2,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { ExamContentGuard } from '../components/exam/ExamContentGuard';
import { DevToolsBlockOverlay } from '../components/exam/DevToolsBlockOverlay';
import { useDevToolsGuard } from '../hooks/useDevToolsGuard';
import { examService, type ResumeAttemptResponse } from '../services/exam.service';
import apiClient from '../lib/api-client';
import { getAttemptToken } from '../lib/attempt-token';

interface ListeningQuestion {
  id: string;
  audioTitle: string;
  /** API path for attempt-scoped listening audio (e.g. /exams/attempts/:id/listening-audio/:questionId), or '' if no audio */
  audioApiPath: string;
  prompt: string;
  options: string[];
  playsAllowed: number;
}

function mapResumeToQuestions(data: ResumeAttemptResponse, attemptId: string | null): ListeningQuestion[] {
  const exam = data.exam;
  if (!exam?.questions?.length) return [];
  return exam.questions.map((q) => {
    const hasAudio = !!q.media?.audio;
    const audioApiPath =
      attemptId && hasAudio ? `/exams/attempts/${attemptId}/listening-audio/${q._id}` : '';
    return {
      id: q._id,
      audioTitle: q.passageTitle || (q.prompt ? q.prompt.slice(0, 50) + (q.prompt.length > 50 ? '…' : '') : q.text?.slice(0, 50) || 'Fragment'),
      audioApiPath,
      prompt: q.prompt || q.text || '',
      options: (q.options || []).map((o) => o.text),
      playsAllowed: q.playsAllowed ?? 2,
    };
  });
}

function getInitialAnswers(data: ResumeAttemptResponse): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const exam = data.exam;
  if (!exam?.questions?.length) return out;
  const saved = data.savedAnswers || [];
  exam.questions.forEach((q, idx) => {
    const savedEntry = saved.find((a) => String(a.questionId) === String(q._id));
    if (savedEntry != null && savedEntry.answer !== undefined && savedEntry.answer !== null) {
      const optText = typeof savedEntry.answer === 'string' ? savedEntry.answer : (savedEntry.answer as any)?.text;
      const options = q.options || [];
      const found = options.findIndex((o) => o.text === optText);
      if (found >= 0) out[q._id] = found;
      else if (typeof savedEntry.answer === 'number' && savedEntry.answer >= 0 && savedEntry.answer < options.length)
        out[q._id] = savedEntry.answer as number;
    }
  });
  return out;
}

export const ListeningExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const { devToolsAllowed, devToolsOpen } = useDevToolsGuard(searchParams);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ListeningQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number | null>>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [playsUsed, setPlaysUsed] = useState<Record<string, number>>({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [listeningAudioObjectUrl, setListeningAudioObjectUrl] = useState<string | null>(null);
  const [audioDurations, setAudioDurations] = useState<Record<string, number>>({});
  const [audioError, setAudioError] = useState(false);
  const [audioReloadKey, setAudioReloadKey] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Mirrors of the playback state for synchronous reads inside event handlers,
  // so a rapid double click or a queued pause/ended event cannot double-count.
  const isPlayingRef = useRef(false);
  const isPausedRef = useRef(false);
  const endedRef = useRef(false);

  useEffect(() => {
    if (!attemptId) {
      navigate('/dashboard');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    examService
      .resumeAttempt(attemptId)
      .then((data) => {
        if (cancelled) return;
        if (data.exam?.type !== 'listening') {
          setLoadError(t('exam.common.notListeningExam'));
          setLoading(false);
          return;
        }
        const mapped = mapResumeToQuestions(data, attemptId);
        if (mapped.length === 0) {
          setLoadError(t('exam.common.noQuestionsInExam'));
          setLoading(false);
          return;
        }
        setQuestions(mapped);
        setAnswers(getInitialAnswers(data));
        setExpiresAt(data.expiresAt);
        const remaining = Math.max(
          0,
          Math.floor((new Date(data.expiresAt).getTime() - Date.now()) / 1000),
        );
        setRemainingSeconds(remaining);
        setLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message || t('exam.common.loadError'),
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [attemptId, navigate, t]);

  const current = questions[currentIndex];

  // Fetch listening audio as blob for current question and create object URL; revoke on change/unmount
  useEffect(() => {
    setAudioError(false);
    if (!current?.audioApiPath) {
      setListeningAudioObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let cancelled = false;
    const attemptToken = attemptId ? getAttemptToken(attemptId) : null;
    apiClient
      .get(current.audioApiPath, {
        responseType: 'blob',
        ...(attemptToken
          ? { headers: { 'x-attempt-token': attemptToken } }
          : {}),
      })
      .then((res) => {
        if (cancelled) return;
        const blob = res.data as Blob;
        setListeningAudioObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAudioError(true);
          setListeningAudioObjectUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
        }
      });
    return () => {
      cancelled = true;
      setListeningAudioObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [current?.id, current?.audioApiPath, audioReloadKey, attemptId]);
  const currentPlaysUsed = current ? playsUsed[current.id] || 0 : 0;
  const currentDuration = current ? audioDurations[current.id] ?? 0 : 0;

  const handleSubmit = useCallback(async () => {
    if (!attemptId || submitting) return;
    setSubmitting(true);
    try {
      await examService.submitExam(attemptId);
      navigate(`/results?attemptId=${attemptId}`);
    } catch (err: any) {
      setSubmitting(false);
      setLoadError(err?.response?.data?.message || t('exam.common.failedToSubmit'));
    }
  }, [attemptId, navigate, submitting, t]);

  useEffect(() => {
    if (!expiresAt || !questions.length || !attemptId) return;
    const expiresAtMs = new Date(expiresAt).getTime();
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        examService.submitExam(attemptId).then(() => navigate(`/results?attemptId=${attemptId}`)).catch(() => {});
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [attemptId, expiresAt, questions.length, navigate]);

  // A change of question is a clean slate: nothing to resume, nothing playing.
  // Layout effect so the reset is applied before paint, never leaving a stale
  // "resumable" flag that could be used on the next question.
  useLayoutEffect(() => {
    isPlayingRef.current = false;
    isPausedRef.current = false;
    endedRef.current = false;
    setIsPlaying(false);
    setIsPaused(false);
    setAudioProgress(0);
  }, [current?.id]);

  useEffect(() => {
    if (!audioRef.current || !current) return;
    const el = audioRef.current;
    const onTimeUpdate = () => setAudioProgress(el.currentTime);
    const onEnded = () => {
      // The play is spent once the fragment finishes; the next play is a new one.
      endedRef.current = true;
      isPlayingRef.current = false;
      isPausedRef.current = false;
      setIsPlaying(false);
      setIsPaused(false);
      setAudioProgress(Number.isFinite(el.duration) ? el.duration : 0);
    };
    const onPause = () => {
      // Pausing only flips the play/pause icon. It never touches the counter:
      // handlePlayAudio owns play accounting, and handleStopAudio owns isPaused.
      isPlayingRef.current = false;
      setIsPlaying(false);
    };
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('ended', onEnded);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('pause', onPause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-binding on question identity and audio element mount is intentional
  }, [current?.id, listeningAudioObjectUrl]);

  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatAudioTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handlePlayAudio = useCallback(() => {
    const el = audioRef.current;
    if (!current || !el) return;

    // Resume a paused, unfinished fragment: continue where it left off and do
    // NOT spend a play. Guard against the ended state just in case a pause and
    // ended event raced.
    if (isPausedRef.current && !endedRef.current) {
      isPausedRef.current = false;
      isPlayingRef.current = true;
      setIsPaused(false);
      setIsPlaying(true);
      el.play().catch(() => {
        isPlayingRef.current = false;
        setIsPlaying(false);
      });
      return;
    }

    // Already playing (e.g. a rapid second click before React re-rendered):
    // ignore instead of starting or counting a second play.
    if (isPlayingRef.current) return;

    // A brand new play is only allowed while plays remain.
    if (currentPlaysUsed >= current.playsAllowed) return;

    isPlayingRef.current = true;
    isPausedRef.current = false;
    endedRef.current = false;
    setIsPaused(false);
    setIsPlaying(true);
    el.currentTime = 0;
    setAudioProgress(0);
    // Count the play only once playback actually starts. A rejected play
    // (autoplay policy, decode error) must not consume one of the allowance.
    el.play()
      .then(() => {
        setPlaysUsed((prev) => {
          const used = prev[current.id] || 0;
          if (used >= current.playsAllowed) return prev;
          return { ...prev, [current.id]: used + 1 };
        });
      })
      .catch(() => {
        isPlayingRef.current = false;
        setIsPlaying(false);
      });
  }, [current, currentPlaysUsed]);

  const handleStopAudio = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    isPlayingRef.current = false;
    setIsPlaying(false);
    // A manual pause leaves a resumable position (unless the fragment ended).
    if (!endedRef.current) {
      isPausedRef.current = true;
      setIsPaused(true);
    }
  }, []);

  const handleSelectOption = useCallback(
    (idx: number) => {
      if (!current || !attemptId) return;
      const optionText = current.options[idx];
      setAnswers((prev) => ({ ...prev, [current.id]: idx }));
      examService.saveAnswer(attemptId, current.id, optionText).catch(() => {});
    },
    [current, attemptId],
  );

  const navigateQuestion = (offset: number) => {
    setCurrentIndex((prev) => {
      const next = prev + offset;
      if (next < 0 || next >= questions.length) return prev;
      return next;
    });
    handleStopAudio();
    setAudioProgress(0);
  };

  const answeredCount = Object.values(answers).filter((v) => v !== null && v !== undefined).length;
  const isLowTime = remainingSeconds < 300;
  // Disable only when neither a new play nor a resume is possible. A paused,
  // unfinished fragment stays resumable even after the last play was spent.
  // `!current` matters during the first render, before resumeAttempt resolves:
  // there is no question yet, so nothing can be played and nothing may be read
  // off `current` (reading `current.playsAllowed` here used to crash the page).
  const playControlDisabled =
    !current ||
    (!isPlaying && !isPaused && currentPlaysUsed >= current.playsAllowed) ||
    !current.audioApiPath ||
    (!!current.audioApiPath && !listeningAudioObjectUrl);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">{t('exam.common.loadingExam')}</p>
        </div>
      </div>
    );
  }

  // No question can only mean the load failed or the exam has none. Render an
  // honest error instead of dereferencing an undefined `current` below.
  if (loadError || !current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">{loadError || t('exam.common.noQuestions')}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            {t('exam.common.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      <DevToolsBlockOverlay show={devToolsOpen} />
      {listeningAudioObjectUrl && (
        <audio
          ref={audioRef}
          src={listeningAudioObjectUrl}
          onLoadedMetadata={() => {
            const el = audioRef.current;
            if (!el || !current) return;
            const duration = el.duration;
            if (Number.isFinite(duration) && duration > 0) {
              setAudioDurations((prev) => ({ ...prev, [current.id]: duration }));
            }
          }}
          onError={() => setAudioError(true)}
        />
      )}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('exam.listening.title')}</h1>
                <p className="text-xs text-gray-500">
                  {questions.length} {t('exam.common.questions')}
                </p>
              </div>
            </div>
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg ${
                isLowTime ? 'bg-error/10 text-error animate-pulse' : 'bg-gray-900 text-white'
              }`}
            >
              <Clock className="w-5 h-5" />
              <span className="font-bold">{formatTime(remainingSeconds)}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 bg-gray-200 h-2 rounded-full">
              <div
                className="h-2 rounded-full bg-indigo-500 transition-all duration-300"
                style={{ width: `${(answeredCount / questions.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {answeredCount}/{questions.length} {t('exam.common.answered')}
            </span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-6 w-full">
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">{current.audioTitle}</h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  currentPlaysUsed >= current.playsAllowed ? 'bg-error/10 text-error' : 'bg-success/10 text-success'
                }`}
              >
                {t('exam.common.playsRemaining', {
                  count: Math.max(0, current.playsAllowed - currentPlaysUsed),
                })}
              </span>
            </div>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
              {current.audioApiPath && audioError ? (
                <div className="flex min-h-[3.5rem] flex-col items-center justify-center gap-2 text-center mb-4">
                  <AlertTriangle className="w-7 h-7 text-error" />
                  <p className="text-sm font-medium text-error">{t('exam.listening.audioError')}</p>
                  <p className="text-xs text-gray-500">{t('exam.listening.audioErrorHint')}</p>
                  <Button variant="tertiary" onClick={() => setAudioReloadKey((k) => k + 1)}>
                    {t('exam.listening.audioRetry')}
                  </Button>
                </div>
              ) : (
                <div className="flex min-h-[3.5rem] items-center gap-4 mb-4">
                  <button
                    onClick={isPlaying ? handleStopAudio : handlePlayAudio}
                    disabled={playControlDisabled}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      playControlDisabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : isPlaying
                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
                    }`}
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Volume2 className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700 tabular-nums">
                        {formatAudioTime(audioProgress)} /{' '}
                        {currentDuration > 0 ? formatAudioTime(currentDuration) : '--:--'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-100"
                        style={{
                          width: `${currentDuration > 0 ? (audioProgress / currentDuration) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <p className="text-center text-xs text-gray-500 mt-3">🎧 {t('exam.common.headphonesNote')}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 font-bold rounded-full text-sm">
                {t('exam.common.question')} {currentIndex + 1}
              </span>
            </div>
            <ExamContentGuard disabled={devToolsAllowed}>
              <p className="text-gray-900 font-medium text-lg mb-6">{current.prompt}</p>
              <div className="space-y-3">
                {current.options.map((opt, idx) => {
                const selected = answers[current.id] === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(idx)}
                    className={`w-full text-left px-4 py-4 rounded-xl border-2 transition-all ${
                      selected
                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 font-bold text-sm ${
                        selected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className={selected ? 'font-medium' : ''}>{opt}</span>
                  </button>
                );
              })}
              </div>
            </ExamContentGuard>
            <div className="flex justify-between mt-8 pt-4 border-t border-gray-100">
              <Button
                variant="tertiary"
                onClick={() => navigateQuestion(-1)}
                disabled={currentIndex === 0}
              >
                ← {t('exam.common.previous')}
              </Button>
              <div className="flex gap-3">
                {currentIndex < questions.length - 1 ? (
                  <Button variant="primary" onClick={() => navigateQuestion(1)}>
                    {t('exam.common.next')} →
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="bg-success hover:bg-success/90"
                    onClick={() => setShowSubmitModal(true)}
                    icon={<CheckCircle className="w-4 h-4" />}
                  >
                    {t('exam.common.submitExam')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4">
            <h3 className="font-bold text-gray-900 mb-3">{t('exam.common.questionNavigator')}</h3>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, idx) => {
                const isAnswered = answers[q.id] !== null && answers[q.id] !== undefined;
                const isCurrent = idx === currentIndex;
                return (
                  <button
                    key={q.id}
                    onClick={() => {
                      handleStopAudio();
                      setAudioProgress(0);
                      setCurrentIndex(idx);
                    }}
                    className={`w-10 h-10 rounded-lg text-sm font-medium flex items-center justify-center transition-all ${
                      isCurrent
                        ? 'bg-indigo-600 text-white ring-2 ring-indigo-600 ring-offset-2'
                        : isAnswered
                          ? 'bg-success/20 text-success'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('exam.common.confirmSubmit')}</h3>
                <p className="text-sm text-gray-500">{t('exam.common.cannotUndo')}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('exam.common.answered')}:</span>
                <span className="font-bold text-success">{answeredCount}/{questions.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('exam.common.notAnswered')}:</span>
                <span className="font-bold text-error">{questions.length - answeredCount}</span>
              </div>
            </div>
            {questions.length - answeredCount > 0 && (
              <p className="text-sm text-error mb-4">
                ⚠️ {t('exam.common.unansweredWarning', { count: questions.length - answeredCount })}
              </p>
            )}
            <div className="flex gap-3">
              <Button variant="tertiary" className="flex-1" onClick={() => setShowSubmitModal(false)} disabled={submitting}>
                {t('exam.common.backToExam')}
              </Button>
              <Button
                variant="primary"
                className="flex-1 bg-success hover:bg-success/90"
                loading={submitting}
                onClick={handleSubmit}
                icon={<CheckCircle className="w-4 h-4" />}
              >
                {t('exam.common.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListeningExamPage;
